import {
  FAQ_SECTIONS,
  GLOSSARY,
  itemGroups,
  resourceRows,
  roleOverviews
} from "../help/helpContent";
import { state } from "../state";
import type { ScreenContext } from "../app";
import { loadShips } from "../../data/loadShips";
import { openBlueprint, type BlueprintSpec } from "../shipBlueprint";
import type { NpcHullTier } from "../../types";

/**
 * The "?" pane (Don's direction): FAQ, glossary, ship overview and item
 * listing, opened from the Expedition View's icon row.
 *
 * Four tabs rather than one long scroll. The content is deliberately large --
 * it is the only in-game explanation of any of these systems -- and a captain
 * who opened it to answer one question should not have to scroll past three
 * other topics to reach theirs.
 *
 * The chosen tab lives in module scope, not game state: it is a UI position,
 * not something worth persisting into a save or syncing between devices.
 */
type Tab = "faq" | "glossary" | "ships" | "items";

let activeTab: Tab = "faq";

const TABS: Array<{ id: Tab; label: string }> = [
  { id: "faq", label: "FAQ" },
  { id: "glossary", label: "Glossary" },
  { id: "ships", label: "Ships" },
  { id: "items", label: "Items & Resources" }
];

export function renderFaqScreen(container: HTMLElement, ctx: ScreenContext): void {
  const wrap = document.createElement("div");
  wrap.className = "faq-wrap";

  wrap.innerHTML = `
    <h1>Captain's Handbook</h1>
    <div class="faq-tabs">
      ${TABS.map(
        (t) => `<button type="button" class="faq-tab ${activeTab === t.id ? "active" : ""}" data-tab="${t.id}">${t.label}</button>`
      ).join("")}
    </div>
    <div class="faq-body">${bodyHtml()}</div>
  `;

  wrap.querySelectorAll<HTMLButtonElement>("[data-tab]").forEach((btn) => {
    btn.addEventListener("click", () => {
      activeTab = btn.dataset.tab as Tab;
      ctx.rerender();
    });
  });

  if (activeTab === "ships") {
    wrap.querySelectorAll<HTMLButtonElement>("[data-bp-role][data-bp-tier]").forEach((btn) => {
      btn.addEventListener("click", () => openBlueprint(playerBlueprintSpec(btn.dataset.bpRole!, btn.dataset.bpTier as NpcHullTier)));
    });
  }

  container.appendChild(wrap);
}

// Builds the full-fidelity blueprint spec straight off the real roster row
// (not the Ships tab's summarized RoleOverview, which only carries combined
// EHP) -- so the blueprint's stat sheet shows Hull/Shield separately, the
// same numbers the Standard-variant table above it is drawn from.
function playerBlueprintSpec(role: string, tier: NpcHullTier): BlueprintSpec {
  const ships = loadShips();
  const s = ships.find((x) => x.role === role && x.hullTier === tier && x.variant === "Standard") ?? ships.find((x) => x.role === role && x.hullTier === tier)!;
  return {
    key: role,
    faction: "Player",
    displayName: s.name,
    tier,
    drawingNo: `SF-${role.slice(0, 3).toUpperCase()}-${tier}`,
    stats: [
      { label: "Hull HP", value: s.hullHp.toLocaleString() },
      { label: "Shield HP", value: s.shieldHp ? s.shieldHp.toLocaleString() : "—" },
      { label: "Shield regen", value: s.shieldRegenPerS ? `${s.shieldRegenPerS.toFixed(1)}/s` : "—" },
      { label: "Repair", value: s.repairPerS ? `${s.repairPerS.toFixed(1)}/s` : "none" },
      { label: "DPS", value: s.dps.toFixed(1) },
      { label: "Cargo", value: `${s.cargoM3.toLocaleString()} m³` },
      { label: "Ore hold", value: `${s.oreHoldM3.toLocaleString()} m³` },
      { label: "Crew", value: s.crew.toLocaleString() },
      { label: "Cost", value: `${s.costCr.toLocaleString()} CR` }
    ]
  };
}

function bodyHtml(): string {
  if (activeTab === "glossary") return glossaryHtml();
  if (activeTab === "ships") return shipsHtml();
  if (activeTab === "items") return itemsHtml();
  return faqHtml();
}

function faqHtml(): string {
  return FAQ_SECTIONS.map(
    (section) => `
      <section class="faq-section">
        <h2>${section.title}</h2>
        ${section.entries
          .map(
            (e) => `
              <details class="faq-entry">
                <summary>${escapeHtml(e.q)}</summary>
                <p>${escapeHtml(e.a)}</p>
              </details>
            `
          )
          .join("")}
      </section>
    `
  ).join("");
}

function glossaryHtml(): string {
  return `
    <section class="faq-section">
      <h2>Glossary</h2>
      <p class="dim">Every term the interface uses, in the sense this game means it.</p>
      <dl class="faq-glossary">
        ${GLOSSARY.map((g) => `<dt>${escapeHtml(g.q)}</dt><dd>${escapeHtml(g.a)}</dd>`).join("")}
      </dl>
    </section>
  `;
}

function shipsHtml(): string {
  const current = state.currentShip;
  const roles = roleOverviews();
  return `
    <section class="faq-section">
      <h2>The six roles</h2>
      <p class="dim">
        Every role exists at all four hull tiers, so a role is the job the ship does and the
        tier is how much of it the ship can do. Tiers are T1 Small, T2 Medium, T3 Large,
        T4 Capital.${current ? ` You are currently flying a <strong>${escapeHtml(current.name)}</strong> (${escapeHtml(current.role)}, ${escapeHtml(current.hullTier)}).` : ""}
      </p>
      ${roles
        .map(
          (r) => `
            <div class="faq-role">
              <h3>${escapeHtml(r.role)}</h3>
              <p>${escapeHtml(r.blurb)}</p>
              <div class="faq-table-scroll">
                <table class="faq-table">
                  <thead>
                    <tr><th>Tier</th><th>EHP</th><th>DPS</th><th>Cargo m³</th><th>Ore Hold m³</th><th>Crew</th><th>Cost CR</th><th></th></tr>
                  </thead>
                  <tbody>
                    ${r.tiers
                      .map(
                        (t) => `<tr${current && current.role === r.role && current.hullTier === t.tier ? ' class="faq-row-current"' : ""}>
                          <td>${t.tier}</td><td>${t.ehp.toLocaleString()}</td><td>${t.dps.toFixed(1)}</td>
                          <td>${t.cargoM3.toLocaleString()}</td><td>${t.oreHoldM3.toLocaleString()}</td>
                          <td>${t.crew.toLocaleString()}</td><td>${t.costCr.toLocaleString()}</td>
                          <td><button type="button" class="sf-bp-trigger" data-bp-role="${escapeHtml(r.role)}" data-bp-tier="${t.tier}">Blueprint</button></td>
                        </tr>`
                      )
                      .join("")}
                  </tbody>
                </table>
              </div>
            </div>
          `
        )
        .join("")}
      <p class="dim">
        Figures are the Standard variant of each tier. Each hull also comes in Aggressive,
        Defensive and Utility variants that trade the same budget around — a little more of
        one thing for a little less of another.
      </p>
    </section>
  `;
}

function itemsHtml(): string {
  const groups = itemGroups();
  const resources = resourceRows();
  return `
    <section class="faq-section">
      <h2>Mined resources</h2>
      <p class="dim">
        The nine things asteroids and deposits contain. They go into your <strong>Ore Hold</strong>,
        never into Cargo. A node's richness multiplies how much is in it, not what it is worth
        per cubic metre.
      </p>
      <div class="faq-table-scroll">
        <table class="faq-table">
          <thead><tr><th>Resource</th><th>Tier</th><th>CR/m³</th><th>Notes</th></tr></thead>
          <tbody>
            ${resources
              .map(
                (r) => `<tr>
                  <td><strong>${escapeHtml(r.name)}</strong></td>
                  <td>T${r.tier}</td>
                  <td>${r.crPerM3.toFixed(2)}</td>
                  <td class="faq-note">${escapeHtml(r.note)}</td>
                </tr>`
              )
              .join("")}
          </tbody>
        </table>
      </div>
    </section>

    <section class="faq-section">
      <h2>Trade goods</h2>
      <p class="dim">
        Everything bought, sold and looted that is not a mined resource. These travel in
        <strong>Cargo</strong>, and one unit is one cubic metre. Within most categories every
        item is worth the same, so what you carry is a question of what a market will actually
        take rather than of picking the "best" item — Munitions is the exception, since drones
        are priced individually by tier. Listed most valuable first.
      </p>
      ${groups
        .map(
          (g) => `
            <div class="faq-role">
              <h3>
                ${escapeHtml(g.category)}
                <span class="pill">${g.minPrice === g.maxPrice ? `${g.minPrice.toFixed(2)} CR` : `${g.minPrice.toFixed(2)}–${g.maxPrice.toFixed(2)} CR`}/unit</span>
              </h3>
              <p>${escapeHtml(g.blurb)}</p>
              <p class="faq-items">${g.items
                .map(
                  (i) =>
                    `<span class="faq-item">${escapeHtml(i.name)}${g.minPrice === g.maxPrice ? "" : ` <span class="dim">${i.pricePerUnit.toFixed(0)} CR</span>`}</span>`
                )
                .join("")}</p>
            </div>
          `
        )
        .join("")}
    </section>
  `;
}

/**
 * Content here is authored, not user input, so this is belt-and-braces rather
 * than a live defence -- but item and ship names come from data files that get
 * edited often, and a stray angle bracket in one of them should render as text
 * instead of quietly breaking the pane.
 */
function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
